'use client';

import { Icon, Tooltip } from '@components/ui';
import CircularProgressBar from '@components/ui/CircularProgressBar/CircularProgessBar';
import clsx from 'clsx';

import styles from './Columns.module.scss';

interface PredictionsCellProps {
  rowIndex: number;
  originalDatasetPrediction: boolean;
  perturbedDatasetsPredictions: Record<string, boolean>;
}

export const PredictionsCell: React.FC<PredictionsCellProps> = ({
  rowIndex,
  originalDatasetPrediction,
  perturbedDatasetsPredictions,
}) => {
  const otherKeys = Object.entries(perturbedDatasetsPredictions);
  const originalValue = originalDatasetPrediction;
  const otherKeysPercentage =
    otherKeys.length > 0 ? (otherKeys.filter(([, value]) => value).length / otherKeys.length) * 100 : 0;

  return (
    <div className={clsx(styles.wrapper, styles.gap10)}>
      <div>
        <Tooltip
          placement={rowIndex === 0 ? 'bottom' : 'top'}
          content={
            <div className={styles.tooltipContent}>
              <div className={styles.tooltipHeader}>Original data predictions</div>
              <div
                className={clsx(styles.miniIcon, {
                  [styles.success]: originalValue,
                  [styles.notStarted]: !originalValue,
                })}
              >
                {originalValue ? <Icon name="check" /> : <Icon name="dash" />}
              </div>
            </div>
          }
          className={styles.tooltipWrapper}
          isFixed
        >
          {originalValue ? (
            <div
              className={clsx(styles.icon, styles.success)}
              data-testid="prediction-original-success"
            >
              <Icon name="check" />
            </div>
          ) : (
            <div data-testid="prediction-original-none">
              <CircularProgressBar
                percentage={0}
                size={32}
                strokeWidth={4}
                includeText
              />
            </div>
          )}
        </Tooltip>
      </div>

      {otherKeys.length > 0 && (
        <div>
          <Tooltip
            placement={rowIndex === 0 ? 'bottom' : 'top'}
            content={
              <div>
                <div className={styles.tooltipPerturbationsHeader}>Perturbation predictions</div>
                <div>
                  {otherKeys.map(([key, value]) => (
                    <div
                      key={key}
                      style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}
                    >
                      <span>• {key}</span>
                      <div
                        className={clsx(styles.miniIcon, {
                          [styles.success]: value,
                          [styles.notStarted]: !value,
                        })}
                      >
                        {value ? <Icon name="check" /> : <Icon name="dash" />}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            }
            className={styles.tooltipWrapper}
            isFixed
          >
            {otherKeysPercentage === 100 ? (
              <div className={clsx(styles.icon, styles.success)}>
                <Icon name="check" />
              </div>
            ) : (
              <CircularProgressBar
                percentage={otherKeysPercentage}
                size={32}
                strokeWidth={4}
                includeText
              />
            )}
          </Tooltip>
        </div>
      )}
    </div>
  );
};
